import { useEffect, useRef } from 'react';

interface IUseSizeAsCSSVariableProps {
  cssVariableName: string;
  size?: 'height' | 'width';
}

interface IUseSizeAsCSSVariableReturn<T extends HTMLElement> {
  ref: React.RefObject<T>;
}

function useSizeAsCSSVariable<T extends HTMLElement>({
  cssVariableName,
  size = 'height',
}: IUseSizeAsCSSVariableProps): IUseSizeAsCSSVariableReturn<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    // Update the CSS variable with the current size of the element.
    function updateSize(): void {
      if (ref.current) {
        const currentSize = ref.current.getBoundingClientRect()[size] ?? 0;
        document.documentElement.style.setProperty(cssVariableName, `${currentSize}px`);
      }
    }

    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);
    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return { ref };
}

export default useSizeAsCSSVariable;
